import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ntd } from '@/lib/format';
import {
  Card,
  Empty,
  PageHeader,
  Stat,
  Tag,
  cellStyle,
  thStyle,
} from '@/components/ui';
import { useAuth } from '@/store/auth';

interface BranchRow {
  branchId: string;
  name: string;
  short: string;
  country: string;
  doneAppointments: number;
  customers: number;
  revenueCents: number;
  cogsCents: number;
  grossProfitCents: number;
  grossMarginPct: number;
  avgTicketCents: number;
}
interface BranchPnl {
  branches: BranchRow[];
  unattributed: { doneAppointments: number; revenueCents: number };
  totals: { revenueCents: number; cogsCents: number; grossProfitCents: number; doneAppointments: number };
}

const marginTone = (p: number): Parameters<typeof Tag>[0]['tone'] =>
  p >= 70 ? 'success' : p >= 50 ? 'warn' : 'danger';

export function BranchPnlPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'TENANT_ADMIN' || user?.role === 'STORE_MANAGER';

  const q = useQuery({
    queryKey: ['branch-pnl'],
    queryFn: () => api.get<BranchPnl>('/finance/branch-pnl'),
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="分店損益" subtitle="各分店毛利貢獻" />
        <Card><Empty>🔒 分店損益屬財務機密,僅管理層可檢視。</Empty></Card>
      </div>
    );
  }

  const d = q.data;
  const maxGP = Math.max(1, ...(d?.branches.map((b) => b.grossProfitCents) ?? [1]));

  return (
    <div>
      <PageHeader title="分店損益" subtitle="各分店服務產值 − 耗材成本 = 毛利貢獻,看哪一店賺、哪一店虧" />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat label="總服務產值" value={ntd(d?.totals.revenueCents ?? 0)} tone="wallet" hint="已完工業績" />
        <Stat label="總耗材成本" value={ntd(d?.totals.cogsCents ?? 0)} tone="warn" />
        <Stat label="總毛利貢獻" value={ntd(d?.totals.grossProfitCents ?? 0)} tone="primary" />
        <Stat
          label="整體毛利率"
          value={`${d && d.totals.revenueCents ? Math.round((d.totals.grossProfitCents / d.totals.revenueCents) * 100) : 0}%`}
          tone="success"
        />
      </div>

      <Card title="各分店毛利貢獻" icon="🏬">
        {!d?.branches.length ? (
          <Empty>尚無分店資料。</Empty>
        ) : (
          <>
            {/* 毛利長條對比 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
              {d.branches.map((b) => (
                <div key={b.branchId} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 96, fontSize: 13, fontWeight: 600 }}>{b.name}</div>
                  <div style={{ flex: 1, background: 'var(--surface-2)', borderRadius: 6, height: 26, overflow: 'hidden' }}>
                    <div
                      className="num"
                      style={{
                        width: `${Math.max((b.grossProfitCents / maxGP) * 100, 2)}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, var(--primary), var(--primary-deep))',
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: 8,
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 700,
                        minWidth: 64,
                      }}
                    >
                      {ntd(b.grossProfitCents)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>分店</th>
                  <th style={thStyle}>完工 / 客數</th>
                  <th style={thStyle}>服務產值</th>
                  <th style={thStyle}>耗材成本</th>
                  <th style={thStyle}>毛利</th>
                  <th style={thStyle}>毛利率</th>
                  <th style={thStyle}>客單價</th>
                </tr>
              </thead>
              <tbody>
                {d.branches.map((b) => (
                  <tr key={b.branchId}>
                    <td style={{ ...cellStyle, fontWeight: 600 }}>
                      {b.name}
                      {b.country !== 'TW' && <Tag tone="default" style={{ marginLeft: 6 }}>{b.country}</Tag>}
                    </td>
                    <td style={cellStyle} className="num">{b.doneAppointments} / {b.customers}</td>
                    <td style={cellStyle} className="num">{ntd(b.revenueCents)}</td>
                    <td style={cellStyle} className="num">{ntd(b.cogsCents)}</td>
                    <td style={cellStyle} className="num" >{ntd(b.grossProfitCents)}</td>
                    <td style={cellStyle} className="num"><Tag tone={marginTone(b.grossMarginPct)}>{b.grossMarginPct}%</Tag></td>
                    <td style={cellStyle} className="num">{ntd(b.avgTicketCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Card>

      {!!d?.unattributed.doneAppointments && (
        <div style={{ fontSize: 12, color: 'var(--warn)', marginBottom: 8 }}>
          ⚠ 有 {d.unattributed.doneAppointments} 筆完工預約未指定分店(產值 {ntd(d.unattributed.revenueCents)}),建議開單時補上分店以利歸屬。
        </div>
      )}
      <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
        ※ 產值 = 已完工療程定價合計(業績,非現金流);成本僅含耗材(扣料規則推導)。<b>不含租金 / 薪資等固定成本</b>,屬「毛利貢獻」非完整損益。
      </div>
    </div>
  );
}
